import PropTypes from "prop-types";
import { cn } from "@/lib/utils";
import {
  channelDef,
  channelTabCounts,
  channelTabTitle,
  isChannelDisconnected,
  visibleInboxChannels,
} from "@/lib/inboxChannels";

function CountBadge({ count, unread, active }) {
  if (!count && !unread) return null;
  if (unread > 0) {
    return (
      <span
        className={cn(
          "inline-flex min-w-[18px] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums",
          active ? "bg-indigo-600 text-white" : "bg-indigo-100 text-indigo-700",
        )}
        data-testid="inbox-tab-unread-badge"
      >
        {unread}
      </span>
    );
  }
  return (
    <span className={cn(
      "text-[10px] tabular-nums",
      active ? "text-indigo-600" : "text-slate-400",
    )}>
      ({count})
    </span>
  );
}

CountBadge.propTypes = {
  count: PropTypes.number,
  unread: PropTypes.number,
  active: PropTypes.bool,
};

export function InboxChannelTabs({ activeChannel, onChange, inboxSummary }) {
  const tabs = visibleInboxChannels(inboxSummary);

  return (
    <div
      className="shrink-0 border-b border-slate-200 bg-white"
      data-testid="inbox-channel-tabs"
    >
      <div className="flex items-center gap-1 overflow-x-auto scrollbar-thin px-2 py-1.5">
        {tabs.map((def) => {
          const active = activeChannel === def.id;
          const { total, unread } = channelTabCounts(def.id, inboxSummary);
          const disconnected = isChannelDisconnected(def.id, inboxSummary);
          const title = channelTabTitle(def.id, inboxSummary);
          const Icon = def.icon;
          
          let tabClass = "text-slate-600 hover:bg-slate-50 hover:text-slate-900";
          if (active) {
            tabClass = "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200";
          } else if (disconnected) {
            tabClass = "text-slate-500 hover:bg-slate-50 hover:text-slate-700";
          }

          return (
            <button
              key={def.id}
              type="button"
              onClick={() => onChange(def.id)}
              title={title}
              aria-label={
                disconnected
                  ? `${def.label}, disconnected — historical conversations available`
                  : def.label
              }
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-2 sm:py-1.5 min-h-10 sm:min-h-0 text-[11.5px] font-medium transition-colors",
                tabClass,
              )}
              data-testid={`inbox-tab-${def.id}`}
              data-disconnected={disconnected ? "true" : undefined}
            >
              <Icon
                size={13}
                strokeWidth={2}
                className={cn("shrink-0", disconnected && !active && "opacity-60")}
              />
              <span>{def.label}</span>
              {disconnected && (
                <span
                  className={cn(
                    "rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide",
                    active
                      ? "bg-amber-100 text-amber-800"
                      : "bg-slate-100 text-slate-500",
                  )}
                  data-testid={`inbox-tab-${def.id}-disconnected`}
                >
                  Disconnected
                </span>
              )}
              <CountBadge count={total} unread={unread} active={active} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

InboxChannelTabs.propTypes = {
  activeChannel: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  inboxSummary: PropTypes.object,
};

export { channelDef };
